// Segment model for the storage donut on the customer dashboard.
//
// Three quantities, and conflating any two of them misleads the customer:
//   burned — written to glass. Permanent, and it consumed medium for good.
//   staged — uploaded, not yet burned. Already spoken for, but not yet consumed.
//   free   — purchased capacity that neither of the above has claimed.
//
// The dashboard previously drew burned against purchased and left staged out
// entirely, so a customer with a large upload waiting to be burned was shown
// room that was in truth already committed.
//
// Kept apart from the rendering so the arithmetic — especially the awkward
// cases of an exceeded quota and of staged data outgrowing what is left — can
// be tested without a DOM.

export type QuotaSegmentKey = 'burned' | 'staged' | 'free';

export interface QuotaSegment {
  key: QuotaSegmentKey;
  bytes: number;
  /** Share of the ring, 0..1. */
  fraction: number;
}

export interface QuotaChartModel {
  segments: QuotaSegment[];
  /** What the ring represents in total. */
  totalBytes: number;
  hasQuota: boolean;
  /** Bytes burned beyond the purchased volume; 0 when within quota. */
  overBytes: number;
  /** Burned share of the PURCHASED volume, 0..1+ (not of the ring). */
  usedFraction: number;
  /** Nothing burned, nothing staged — there is no meaningful chart to draw. */
  empty: boolean;
}

export interface QuotaInput {
  hasQuota: boolean;
  purchasedBytes: number;
  burnedBytes: number;
  stagedBytes: number;
}

/**
 * Turn the quota figures into ring segments.
 *
 * The ring always sums to burned + staged + free. When burned and staged
 * together exceed the purchased volume, free collapses to zero and the ring
 * grows past the purchase instead of clipping the excess away — showing less
 * than was really written would be the one genuinely harmful rounding here.
 */
export function quotaChartModel(q: QuotaInput): QuotaChartModel {
  const burned = Math.max(0, q.burnedBytes || 0);
  const staged = Math.max(0, q.stagedBytes || 0);
  const purchased = Math.max(0, q.purchasedBytes || 0);
  const hasQuota = q.hasQuota && purchased > 0;

  const free = hasQuota ? Math.max(0, purchased - burned - staged) : 0;
  const totalBytes = burned + staged + free;

  const share = (n: number) => (totalBytes > 0 ? n / totalBytes : 0);
  const segments: QuotaSegment[] = [
    { key: 'burned', bytes: burned, fraction: share(burned) },
    { key: 'staged', bytes: staged, fraction: share(staged) },
    { key: 'free', bytes: free, fraction: share(free) },
  ];

  return {
    segments,
    totalBytes,
    hasQuota,
    overBytes: hasQuota ? Math.max(0, burned - purchased) : 0,
    usedFraction: hasQuota ? burned / purchased : 0,
    empty: totalBytes === 0,
  };
}

export interface DonutArc {
  /** stroke-dasharray for the segment. */
  dashArray: string;
  /** stroke-dashoffset placing it after the preceding segments. */
  dashOffset: number;
}

/**
 * Dash geometry for each fraction on a circle of the given circumference.
 *
 * Segments are laid out head to tail; a zero-length segment still gets an entry
 * so callers can map segments to arcs by index without filtering first.
 */
export function donutArcs(fractions: number[], circumference: number): DonutArc[] {
  let consumed = 0;
  return fractions.map((f) => {
    const length = Math.max(0, f) * circumference;
    const arc: DonutArc = {
      dashArray: `${length} ${circumference - length}`,
      dashOffset: -consumed,
    };
    consumed += length;
    return arc;
  });
}
